import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Eye, EyeOff } from 'lucide-react';
import { useToast } from '@centraoneplus/ui';
import { useAuthStore } from '@stores/authStore';
import { platformAuthService, type PlatformAuthResponse } from '@services/platform-auth.service';
import { CredentialEncryptionError } from '@/lib/credentialEncryption';
import { PlatformAuthTabs } from './PlatformAuthTabs';

export default function PlatformLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();
  const platformLogin = useAuthStore((state) => state.platformLogin);
  const toast = useToast();

  useEffect(() => {
    if (!requiresMfa || secondsLeft <= 0) return;
    const id = window.setInterval(() => {
      setSecondsLeft((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [requiresMfa, secondsLeft]);

  const expired = requiresMfa && secondsLeft <= 0;
  const timerLabel = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;

  const completeLogin = (response: PlatformAuthResponse) => {
    const { user, accessToken } = response;
    platformLogin(
      {
        id: user.id,
        email: user.email,
        name: user.displayName || user.firstName || user.email,
        role: user.platformRole,
        tenantId: '',
        isPlatformUser: true,
        platformRole: user.platformRole,
        isPrimary: !!user.isPrimary,
      },
      accessToken,
    );
    toast.success('Welcome!', `Signed in as ${user.displayName || user.email}`);
    navigate('/platform/tenants');
  };

  const handlePasswordSubmit = async () => {
    try {
      const response = await platformAuthService.login({ email, password });
      if (response.requiresMfa && response.mfaToken) {
        setMfaToken(response.mfaToken);
        setMaskedEmail(response.maskedEmail || '');
        setSecondsLeft(response.expiresInSeconds || 600);
        setMfaCode('');
        setRequiresMfa(true);
        setError('');
        toast.success('Code sent', `We emailed a code to ${response.maskedEmail || 'your email'}`);
        return;
      }
      completeLogin(response);
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string; error?: { code?: string; message?: string } }>;
      if (err instanceof CredentialEncryptionError) {
        setError(err.message);
      } else if (axiosError.response?.status === 401) {
        setError('Invalid email or password');
      } else if (axiosError.response?.status === 429) {
        setError('Too many login attempts. Please try again later.');
      } else if (axiosError.response?.data?.message) {
        setError(axiosError.response.data.message);
      } else {
        setError('An error occurred. Please try again.');
      }
    }
  };

  const handleOtpSubmit = async () => {
    if (expired) {
      setError('Your code has expired. Please request a new one.');
      return;
    }
    try {
      const response = await platformAuthService.verifyLoginOtp(mfaToken, mfaCode);
      completeLogin(response);
    } catch (err) {
      const axiosError = err as AxiosError<{ error?: { code?: string; message?: string } }>;
      const code = axiosError.response?.data?.error?.code;
      if (code === 'OTP_EXPIRED' || code === 'OTP_NOT_FOUND' || code === 'OTP_MAX_ATTEMPTS') {
        setSecondsLeft(0);
        const msg =
          code === 'OTP_MAX_ATTEMPTS'
            ? 'Too many incorrect attempts. Please request a new code.'
            : 'Your code has expired. Please request a new one.';
        setError(msg);
        toast.error('Code no longer valid', msg);
      } else if (code === 'MFA_TOKEN_INVALID') {
        handleBackToLogin();
        setError('Your login session has expired. Please sign in again.');
        toast.error('Session expired', 'Please sign in again.');
      } else {
        setMfaCode('');
        setError('Enter the correct code.');
        toast.error('Incorrect code', 'Enter the correct code.');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (requiresMfa) {
        await handleOtpSubmit();
      } else {
        await handlePasswordSubmit();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setResending(true);
    try {
      const response = await platformAuthService.resendLoginOtp(mfaToken);
      setMfaToken(response.mfaToken || mfaToken);
      setMaskedEmail(response.maskedEmail || maskedEmail);
      setSecondsLeft(response.expiresInSeconds || 600);
      setMfaCode('');
      toast.success('New code sent', `We emailed a new code to ${response.maskedEmail || maskedEmail}`);
    } catch (err) {
      const axiosError = err as AxiosError<{ error?: { code?: string; message?: string } }>;
      const code = axiosError.response?.data?.error?.code;
      if (code === 'OTP_RESEND_COOLDOWN') {
        setError(axiosError.response?.data?.error?.message || 'Please wait a moment before requesting another code.');
      } else if (code === 'MFA_TOKEN_INVALID') {
        handleBackToLogin();
        setError('Your login session has expired. Please sign in again.');
      } else {
        setError('Could not send a new code. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  const handleBackToLogin = () => {
    setRequiresMfa(false);
    setMfaCode('');
    setMfaToken('');
    setMaskedEmail('');
    setSecondsLeft(0);
    setError('');
  };

  return (
    <div>
      <div className="mb-6 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-[12px] bg-[var(--co-brass-wash)] mb-3">
          <svg className="w-6 h-6 text-[var(--co-brass-ink)]" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
          </svg>
        </div>
        <h2 className="font-display text-[22px] font-extrabold tracking-[-0.02em] text-[var(--co-ink)]">
          Platform Admin Login
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Access the CentraOne platform management console
        </p>
      </div>

      {!requiresMfa && <PlatformAuthTabs />}

      <form className="space-y-6" onSubmit={handleSubmit}>
        {error && (
          <div className="px-4 py-3 rounded-[10px] bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {!requiresMfa ? (
            <>
              <div>
                <label htmlFor="platform-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email address
                </label>
                <input
                  id="platform-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input !rounded-[10px] mt-1 dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                  placeholder="Enter your platform admin email"
                />
              </div>

              <div>
                <label htmlFor="platform-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Password
                </label>
                {/* Hide the native Edge/IE password reveal control so only the custom toggle shows */}
                <style>{`#platform-password::-ms-reveal, #platform-password::-ms-clear { display: none; }`}</style>
                <div className="relative mt-1">
                  <input
                    id="platform-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input !rounded-[10px] pr-10 dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    title={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-800 dark:text-white hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div>
              <label htmlFor="platform-mfaCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Enter verification code
              </label>
              <p className="mt-1 mb-2 text-sm text-gray-500 dark:text-gray-400">
                We sent a 6-digit code to <span className="font-medium text-gray-700 dark:text-gray-200">{maskedEmail}</span>.
              </p>
              <input
                id="platform-mfaCode"
                name="mfaCode"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                required
                disabled={expired}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="input !rounded-[10px] mt-1 text-center text-2xl tracking-widest dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 disabled:opacity-50"
                placeholder="000000"
                maxLength={6}
              />
              <div className="mt-2 flex items-center justify-between text-sm">
                {expired ? (
                  <span className="text-red-600 dark:text-red-400">Code expired</span>
                ) : (
                  <span className="text-gray-500 dark:text-gray-400">
                    Code expires in <span className="font-medium tabular-nums">{timerLabel}</span>
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending || (!expired && secondsLeft > 0)}
                  className="text-[var(--co-brass-ink)] hover:text-[var(--co-brass)] disabled:opacity-40 disabled:cursor-not-allowed"
                  title={!expired && secondsLeft > 0 ? 'You can resend once the code expires' : 'Send a new code'}
                >
                  {resending ? 'Sending…' : 'Resend code'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <button
            type="submit"
            disabled={loading || (requiresMfa && (mfaCode.length !== 6 || expired))}
            className="btn-primary w-full !rounded-[10px] h-11 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Signing in...' : requiresMfa ? 'Verify' : 'Sign in'}
          </button>

          {requiresMfa && (
            <button
              type="button"
              onClick={handleBackToLogin}
              className="btn-secondary w-full !rounded-[10px] h-11 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
            >
              Back to login
            </button>
          )}
        </div>

        <p className="text-center text-sm text-gray-500 dark:text-gray-400">
          <Link to="/login" className="text-[var(--co-brass-ink)] hover:text-[var(--co-brass)] font-medium">
            Back to Tenant Login
          </Link>
        </p>
      </form>
    </div>
  );
}
